/**
 * Canonical sparse multivariate polynomials over Q(i), and rational functions
 * as (numerator, denominator) pairs (#361, K1).
 *
 * "Canonical" is the point: terms are sorted by monomial key, monomials list
 * their variables in name order, zero coefficients are never stored. Two
 * polynomials are equal as polynomials iff `polyEqual` says so -- no
 * normalisation heuristic, no `simplify`. Expansion is exact (BigInt) and
 * bounded: a polynomial that would exceed `MAX_TERMS`, or a power past
 * `MAX_EXPONENT`, throws `KernelLimitError` and the kernel answers
 * `undecided` rather than guess.
 */

import { KernelLimitError, MAX_EXPONENT, type Expr } from './expr.js';
import {
  C0,
  C1,
  cAdd,
  cDiv,
  cEq,
  cIsZero,
  cKey,
  cMul,
  cNeg,
  type C,
} from './numbers.js';

export type Mono = readonly (readonly [string, bigint])[];

export interface PolyTerm {
  readonly mono: Mono;
  readonly coef: C;
}

export type Poly = readonly PolyTerm[];

export const MAX_TERMS = 4000;

const monoKey = (m: Mono): string => m.map(([name, e]) => `${name}^${e.toString()}`).join('*');

function fromMap(map: Map<string, PolyTerm>): Poly {
  const out: PolyTerm[] = [];
  for (const term of map.values()) if (!cIsZero(term.coef)) out.push(term);
  if (out.length > MAX_TERMS) throw new KernelLimitError('polynomial_too_large');
  out.sort((x, y) => {
    const kx = monoKey(x.mono);
    const ky = monoKey(y.mono);
    return kx < ky ? -1 : kx > ky ? 1 : 0;
  });
  return out;
}

export const POLY_ZERO: Poly = [];
export const polyConst = (v: C): Poly => (cIsZero(v) ? POLY_ZERO : [{ mono: [], coef: v }]);
export const POLY_ONE: Poly = polyConst(C1);
export const polyVar = (name: string): Poly => [{ mono: [[name, 1n]], coef: C1 }];

export const polyIsZero = (p: Poly): boolean => p.length === 0;

/** A nonzero constant polynomial, if `p` is one. */
export function polyAsConstant(p: Poly): C | undefined {
  if (p.length === 0) return C0;
  if (p.length === 1 && (p[0] as PolyTerm).mono.length === 0) return (p[0] as PolyTerm).coef;
  return undefined;
}

export function polyAdd(a: Poly, b: Poly): Poly {
  const map = new Map<string, PolyTerm>();
  for (const term of a) map.set(monoKey(term.mono), term);
  for (const term of b) {
    const key = monoKey(term.mono);
    const existing = map.get(key);
    map.set(key, existing === undefined ? term : { mono: term.mono, coef: cAdd(existing.coef, term.coef) });
  }
  return fromMap(map);
}

export const polyNeg = (a: Poly): Poly => a.map((t) => ({ mono: t.mono, coef: cNeg(t.coef) }));
export const polySub = (a: Poly, b: Poly): Poly => polyAdd(a, polyNeg(b));

function monoMul(a: Mono, b: Mono): Mono {
  const exps = new Map<string, bigint>();
  for (const [name, e] of a) exps.set(name, e);
  for (const [name, e] of b) exps.set(name, (exps.get(name) ?? 0n) + e);
  return [...exps.entries()].sort((x, y) => (x[0] < y[0] ? -1 : x[0] > y[0] ? 1 : 0)).map(([name, e]) => [name, e] as const);
}

export function polyMul(a: Poly, b: Poly): Poly {
  if (a.length === 0 || b.length === 0) return POLY_ZERO;
  if (a.length * b.length > MAX_TERMS * MAX_TERMS) throw new KernelLimitError('polynomial_too_large');
  const map = new Map<string, PolyTerm>();
  for (const x of a) {
    for (const y of b) {
      const mono = monoMul(x.mono, y.mono);
      const key = monoKey(mono);
      const coef = cMul(x.coef, y.coef);
      const existing = map.get(key);
      map.set(key, existing === undefined ? { mono, coef } : { mono, coef: cAdd(existing.coef, coef) });
    }
  }
  return fromMap(map);
}

export function polyPow(p: Poly, e: bigint): Poly {
  if (e < 0n || e > MAX_EXPONENT) throw new KernelLimitError('exponent_too_large');
  let result = POLY_ONE;
  let base = p;
  let k = e;
  while (k > 0n) {
    if ((k & 1n) === 1n) result = polyMul(result, base);
    k >>= 1n;
    if (k > 0n) base = polyMul(base, base);
  }
  return result;
}

export function polyScale(p: Poly, by: C): Poly {
  return p.map((t) => ({ mono: t.mono, coef: cMul(t.coef, by) })).filter((t) => !cIsZero(t.coef));
}

export function polyEqual(a: Poly, b: Poly): boolean {
  if (a.length !== b.length) return false;
  for (let k = 0; k < a.length; k += 1) {
    const x = a[k] as PolyTerm;
    const y = b[k] as PolyTerm;
    if (monoKey(x.mono) !== monoKey(y.mono) || !cEq(x.coef, y.coef)) return false;
  }
  return true;
}

/** Exact evaluation; a variable missing from `env` is an error (never silently 0). */
export function polyEval(p: Poly, env: Readonly<Record<string, C>>): C | undefined {
  let total = C0;
  for (const term of p) {
    let value = term.coef;
    for (const [name, e] of term.mono) {
      if (!Object.hasOwn(env, name)) return undefined;
      let factor = C1;
      for (let k = 0n; k < e; k += 1n) factor = cMul(factor, env[name] as C);
      value = cMul(value, factor);
    }
    total = cAdd(total, value);
  }
  return total;
}

export function polyVars(p: Poly): string[] {
  const names = new Set<string>();
  for (const term of p) for (const [name] of term.mono) names.add(name);
  return [...names].sort();
}

/** Total degree (0 for constants and for the zero polynomial). */
export function polyDegree(p: Poly): bigint {
  let best = 0n;
  for (const term of p) {
    let d = 0n;
    for (const [, e] of term.mono) d += e;
    if (d > best) best = d;
  }
  return best;
}

export function polyToString(p: Poly): string {
  if (p.length === 0) return '0';
  return p
    .map((t) => (t.mono.length === 0 ? `(${cKey(t.coef)})` : `(${cKey(t.coef)})*${monoKey(t.mono)}`))
    .join(' + ');
}

/** A rational function num/den. `den` is never the zero polynomial. */
export interface RationalForm {
  readonly num: Poly;
  readonly den: Poly;
}

export class UndefinedEverywhereError extends Error {
  override readonly name = 'UndefinedEverywhereError';
  constructor() {
    super('expression divides by the zero polynomial');
  }
}

function normaliseRF(num: Poly, den: Poly): RationalForm {
  const constant = polyAsConstant(den);
  if (constant !== undefined && !cIsZero(constant)) {
    const inverse = cDiv(C1, constant);
    return { num: polyScale(num, inverse), den: POLY_ONE };
  }
  return { num, den };
}

/**
 * Expand an expression to a rational form, recording in `excluded` every
 * polynomial that must be nonzero for the expression to be defined (the
 * numerator of each divisor). Denominators created by an inner division are
 * already in `excluded` when they are used.
 */
export function toRationalForm(e: Expr, excluded: Poly[]): RationalForm {
  switch (e.k) {
    case 'const':
      return { num: polyConst(e.v), den: POLY_ONE };
    case 'var':
      return { num: polyVar(e.name), den: POLY_ONE };
    case 'neg': {
      const a = toRationalForm(e.a, excluded);
      return { num: polyNeg(a.num), den: a.den };
    }
    case 'add':
    case 'sub': {
      const a = toRationalForm(e.a, excluded);
      const b = toRationalForm(e.b, excluded);
      const left = polyMul(a.num, b.den);
      const right = polyMul(b.num, a.den);
      return normaliseRF(e.k === 'add' ? polyAdd(left, right) : polySub(left, right), polyMul(a.den, b.den));
    }
    case 'mul': {
      const a = toRationalForm(e.a, excluded);
      const b = toRationalForm(e.b, excluded);
      return normaliseRF(polyMul(a.num, b.num), polyMul(a.den, b.den));
    }
    case 'div': {
      const a = toRationalForm(e.a, excluded);
      const b = toRationalForm(e.b, excluded);
      return divideForms(a, b, excluded);
    }
    case 'pow': {
      if (e.exp > MAX_EXPONENT || e.exp < -MAX_EXPONENT) throw new KernelLimitError('exponent_too_large');
      const base = toRationalForm(e.base, excluded);
      if (e.exp >= 0n) return normaliseRF(polyPow(base.num, e.exp), polyPow(base.den, e.exp));
      return divideForms({ num: POLY_ONE, den: POLY_ONE }, { num: polyPow(base.num, -e.exp), den: polyPow(base.den, -e.exp) }, excluded, base.num);
    }
  }
}

function divideForms(a: RationalForm, b: RationalForm, excluded: Poly[], divisorNumerator: Poly = b.num): RationalForm {
  if (polyIsZero(divisorNumerator)) throw new UndefinedEverywhereError();
  if (polyAsConstant(divisorNumerator) === undefined) addExcluded(excluded, divisorNumerator);
  return normaliseRF(polyMul(a.num, b.den), polyMul(a.den, b.num));
}

function addExcluded(excluded: Poly[], p: Poly): void {
  if (!excluded.some((existing) => polyEqual(existing, p))) excluded.push(p);
}

/** Cross-multiplication: num_a * den_b - num_b * den_a. Zero iff the two forms are the same rational function. */
export function crossDifference(a: RationalForm, b: RationalForm): Poly {
  return polySub(polyMul(a.num, b.den), polyMul(b.num, a.den));
}

export function formsEqual(a: RationalForm, b: RationalForm): boolean {
  return polyIsZero(crossDifference(a, b));
}
